import { useState } from 'react'
import {
    Alert,
    Button,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Stack,
    TextField,
    Typography,
} from '@mui/material'
import { GridRenderEditCellParams } from '@mui/x-data-grid-pro'
import { formatDurationHms } from '@utils'

const MS_PER_SECOND = 1000
const MS_PER_MINUTE = 60 * MS_PER_SECOND
const MS_PER_HOUR = 60 * MS_PER_MINUTE

type DurationParts = {
    hours: string
    minutes: string
    seconds: string
}

function millisecondsToParts(
    totalMilliseconds: number | null | undefined
): DurationParts {
    const safeMilliseconds =
        typeof totalMilliseconds === 'number' && totalMilliseconds > 0
            ? totalMilliseconds
            : 0
    const totalSeconds = Math.floor(safeMilliseconds / MS_PER_SECOND)

    return {
        hours: String(Math.floor(totalSeconds / 3600)),
        minutes: String(Math.floor((totalSeconds % 3600) / 60)),
        seconds: String(totalSeconds % 60),
    }
}

/**
 * Null rather than a number when any box is empty, not a whole number, or out
 * of range. Minutes and seconds are capped at 59 so "90 minutes" has to be
 * written as 1 and 30, which is the point of splitting the field up: every
 * part has to be typed deliberately and none of them can absorb a stray paste.
 */
function partsToMilliseconds(parts: DurationParts): number | null {
    const read = (raw: string, highest: number): number | null => {
        const trimmed = raw.trim()
        if (!/^\d{1,2}$/.test(trimmed)) return null
        const value = Number(trimmed)
        return value > highest ? null : value
    }

    const hours = read(parts.hours, 23)
    const minutes = read(parts.minutes, 59)
    const seconds = read(parts.seconds, 59)

    if (hours === null || minutes === null || seconds === null) return null

    return (
        hours * MS_PER_HOUR + minutes * MS_PER_MINUTE + seconds * MS_PER_SECOND
    )
}

/**
 * The duration column's editor. It opens a dialog rather than editing in
 * place, because the stored value is milliseconds: the cell reads "1:00:54"
 * but a plain editor puts `3654000` in the box, and a dropped digit there is
 * an hour of somebody's free recording allowance. Three boxes, each holding a
 * part a person can actually read, and the old and new values side by side
 * before anything is sent.
 *
 * Commits through the grid's normal edit flow, so `processRowUpdate` on the
 * grid still does the saving and still reverts the row if the save fails.
 */
export default function DurationEditCell(props: GridRenderEditCellParams) {
    const { id, field, value, api } = props
    const [parts, setParts] = useState<DurationParts>(() =>
        millisecondsToParts(value as number | null | undefined)
    )

    const newDurationMs = partsToMilliseconds(parts)
    const currentDurationMs = typeof value === 'number' ? value : 0

    /**
     * What saving without touching anything would produce. Compare against
     * this rather than the stored value: a real recording is 1:00:54.499, not
     * 1:00:54.000, so comparing against the raw figure leaves Save live the
     * instant the dialog opens and lets an accidental click shave the
     * remainder off. Null when the stored value is out of range, which leaves
     * Save available so somebody can correct it, and is the whole point of
     * the screen.
     */
    const baselineMs = partsToMilliseconds(
        millisecondsToParts(currentDurationMs)
    )

    const close = () =>
        api.stopCellEditMode({ id, field, ignoreModifications: true })

    const save = async () => {
        if (newDurationMs === null) return
        // setEditCellValue answers false if the grid refused the value.
        // Closing anyway would leave the dialog gone, nothing changed and
        // nothing said, which is the one outcome this screen must not have.
        const accepted = await api.setEditCellValue({
            id,
            field,
            value: newDurationMs,
        })
        if (!accepted) return
        api.stopCellEditMode({ id, field })
    }

    const setPart = (name: keyof DurationParts) => (raw: string) =>
        setParts((current) => ({ ...current, [name]: raw }))

    return (
        <Dialog open onClose={close} maxWidth="xs" fullWidth>
            <DialogTitle>Correct upload duration</DialogTitle>
            <DialogContent>
                <Alert severity="warning" sx={{ mb: 2 }}>
                    This is the figure mp-server adds up for the user&rsquo;s
                    free recording allowance, not a display label. Changing it
                    can move them either side of the limit, and either free up
                    or use up hours they currently have.
                </Alert>

                <Stack direction="row" spacing={1} sx={{ mb: 2 }}>
                    <TextField
                        label="Hours"
                        value={parts.hours}
                        onChange={(event) =>
                            setPart('hours')(event.target.value)
                        }
                        inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                        size="small"
                        fullWidth
                        autoFocus
                    />
                    <TextField
                        label="Minutes"
                        value={parts.minutes}
                        onChange={(event) =>
                            setPart('minutes')(event.target.value)
                        }
                        inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                        size="small"
                        fullWidth
                    />
                    <TextField
                        label="Seconds"
                        value={parts.seconds}
                        onChange={(event) =>
                            setPart('seconds')(event.target.value)
                        }
                        inputProps={{ inputMode: 'numeric', maxLength: 2 }}
                        size="small"
                        fullWidth
                    />
                </Stack>

                <Typography variant="body2" color="text.secondary">
                    Currently {formatDurationHms(currentDurationMs) || '0:00'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                    {newDurationMs === null
                        ? 'Enter whole numbers. Hours go up to 23, minutes and seconds up to 59.'
                        : `Changing to ${formatDurationHms(newDurationMs) || '0:00'}`}
                </Typography>
            </DialogContent>
            <DialogActions>
                <Button onClick={close}>Cancel</Button>
                <Button
                    onClick={save}
                    variant="contained"
                    disabled={
                        newDurationMs === null || newDurationMs === baselineMs
                    }
                >
                    Save duration
                </Button>
            </DialogActions>
        </Dialog>
    )
}
